/**
 * Legacy Baby Tracker CSV export parsing (spec section 6).
 *
 * Pure and side-effect free: reads a ZIP into bounded, decoded CSV records and
 * analyses them against an explicit IANA timezone. Nothing here touches the
 * database; see importBatch.ts for identity hashing and the atomic commit.
 *
 * Timestamps are parsed explicitly as day-first `d/M/yy h:mm a` (never with
 * Date.parse). Non-existent local times (DST gaps) are rejected and ambiguous
 * ones (DST overlaps) require an explicit caller choice.
 */
import JSZip from 'jszip';
import Papa from 'papaparse';
import { sha256Hex } from '@/lib/ids';
import { resolveZoned } from '@/lib/time';
import type { DiaperStatus, EventType } from '@/lib/schemas';

export const LEGACY_SOURCE_SYSTEM = 'baby_tracker_csv';

export interface ImportLimits {
  /** Maximum size of the ZIP file itself. */
  maxArchiveBytes: number;
  /** Maximum decompressed size of any single entry. */
  maxEntryBytes: number;
  /** Maximum decompressed size of all CSV entries together. */
  maxTotalBytes: number;
  /** Maximum number of entries (files and folders) in the archive. */
  maxEntries: number;
  /** Maximum number of data rows across all recognized files. */
  maxRows: number;
}

export const DEFAULT_LIMITS: ImportLimits = {
  maxArchiveBytes: 10 * 1024 * 1024,
  maxEntryBytes: 10 * 1024 * 1024,
  maxTotalBytes: 25 * 1024 * 1024,
  maxEntries: 64,
  maxRows: 50_000,
};

/** Longest plausible single sleep in the legacy export (minutes). */
export const MAX_SLEEP_MINUTES = 24 * 60;

export class LegacyImportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LegacyImportError';
  }
}

// ---------------------------------------------------------------------------
// Text normalization
// ---------------------------------------------------------------------------

// Every Unicode space separator plus tab/line breaks, NBSP, narrow NBSP
// (U+202F, used before am/pm by the source), figure space and the BOM.
const UNICODE_WS = /[\s\u00A0\u1680\u2000-\u200B\u2028\u2029\u202F\u205F\u3000\uFEFF]+/g;

/** Collapse any run of Unicode whitespace to one ASCII space and trim. */
export function normalizeWhitespace(value: string): string {
  return value.normalize('NFC').replace(UNICODE_WS, ' ').trim();
}

/** Notes are user content: preserve every code point, space and line break. */
export function normalizeNote(value: string): string {
  return value;
}

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

type Column = 'baby' | 'time' | 'amount' | 'duration' | 'status' | 'note';

const SCHEMAS: Record<EventType, Record<string, Column>> = {
  formula: { baby: 'baby', time: 'time', 'amount (ml)': 'amount', note: 'note' },
  sleep: { baby: 'baby', time: 'time', 'duration (min)': 'duration', note: 'note' },
  diaper: { baby: 'baby', time: 'time', status: 'status', note: 'note' },
};

const TYPE_ORDER: EventType[] = ['formula', 'sleep', 'diaper'];

function headerKey(h: string): string {
  return normalizeWhitespace(h).toLowerCase();
}

/** Recognize a file by its header row (not its name). Extra columns are ignored. */
export function detectSchema(header: string[]): { type: EventType; columns: Partial<Record<Column, number>> } | null {
  const keys = header.map(headerKey);
  for (const type of TYPE_ORDER) {
    const schema = SCHEMAS[type];
    const columns: Partial<Record<Column, number>> = {};
    let ok = true;
    for (const [name, col] of Object.entries(schema)) {
      const idx = keys.indexOf(name);
      if (idx < 0 || keys.indexOf(name, idx + 1) >= 0) {
        ok = false;
        break;
      }
      columns[col] = idx;
    }
    if (ok) return { type, columns };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Value parsing
// ---------------------------------------------------------------------------

export interface LocalStamp {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

const STAMP_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4}) (\d{1,2}):(\d{2}) ?([ap])\.?m\.?$/i;

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Explicit `d/M/yy h:mm a` parse (day first). Returns a reason string on failure. */
export function parseLegacyTimestamp(raw: string): LocalStamp | string {
  const value = normalizeWhitespace(raw);
  if (!value) return 'Time is empty';
  const m = STAMP_RE.exec(value);
  if (!m) return `Time "${value}" is not in the expected d/M/yy h:mm am/pm format`;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
  const hour12 = Number(m[4]);
  const minute = Number(m[5]);
  if (month < 1 || month > 12) return `Time "${value}" has an invalid month`;
  if (day < 1 || day > daysInMonth(year, month)) return `Time "${value}" is not a real calendar date`;
  if (hour12 < 1 || hour12 > 12 || minute > 59) return `Time "${value}" has an invalid clock time`;
  const pm = m[6].toLowerCase() === 'p';
  const hour = (hour12 % 12) + (pm ? 12 : 0);
  return { year, month, day, hour, minute };
}

const NUMBER_RE = /^\d+(?:\.\d+)?$/;

function parseNonNegative(raw: string, label: string, unit: string): number | string {
  const value = normalizeWhitespace(raw);
  if (!value) return `${label} is empty`;
  if (!NUMBER_RE.test(value)) return `${label} "${value}" is not a non-negative number of ${unit}`;
  const n = Number(value);
  if (!Number.isFinite(n)) return `${label} "${value}" is not a finite number`;
  return n;
}

const DIAPER_MAP: Record<string, DiaperStatus> = {
  wet: 'wet',
  pee: 'wet',
  urine: 'wet',
  dirty: 'dirty',
  poo: 'dirty',
  poop: 'dirty',
  soiled: 'dirty',
  mixed: 'mixed',
  both: 'mixed',
  'wet and dirty': 'mixed',
  'wet & dirty': 'mixed',
  'wet + dirty': 'mixed',
  'wet+dirty': 'mixed',
  'wet/dirty': 'mixed',
  'dirty and wet': 'mixed',
};

/** Case-insensitive legacy status mapping; null if unknown. */
export function mapDiaperStatus(raw: string): DiaperStatus | null {
  return DIAPER_MAP[normalizeWhitespace(raw).toLowerCase()] ?? null;
}

// ---------------------------------------------------------------------------
// Archive reading
// ---------------------------------------------------------------------------

export interface LegacyFile {
  /** Path inside the archive. */
  path: string;
  /** Stable source file identifier: normalized, lower-cased base name. */
  fileId: string;
  type: EventType;
  columns: Partial<Record<Column, number>>;
  header: string[];
  /** Data records (header excluded) with their 1-based CSV record number. */
  records: Array<{ row: number; fields: string[]; error?: string }>;
  warnings: string[];
}

export interface FileProblem {
  path: string;
  reason: string;
}

export interface LegacyArchive {
  name: string;
  size: number;
  /** SHA-256 of the archive bytes, for the audit batch. */
  sha256: string;
  files: LegacyFile[];
  /** Entries that were not imported (non-CSV, unrecognized headers, unreadable). */
  skipped: FileProblem[];
}

type StreamableEntry = JSZip.JSZipObject & {
  internalStream(type: 'uint8array'): JSZip.JSZipStreamHelper<Uint8Array>;
  _data?: { uncompressedSize?: number; crc32?: number };
};

// Check integrity only after bounded decompression. JSZip's checkCRC32 option
// eagerly inflates every entry (including ignored files) before our limits run.
function crc32(bytes: Uint8Array): number {
  let crc = -1;
  for (const byte of bytes) {
    crc ^= byte;
    for (let bit = 0; bit < 8; bit++) crc = (crc >>> 1) ^ ((crc & 1) ? 0xedb88320 : 0);
  }
  return (crc ^ -1) >>> 0;
}

/** Decompress one entry while enforcing a byte budget (guards against ZIP bombs). */
function readEntryBounded(entry: JSZip.JSZipObject, limit: number): Promise<Uint8Array> {
  const declared = (entry as StreamableEntry)._data?.uncompressedSize;
  if (typeof declared === 'number' && declared > limit)
    return Promise.reject(new LegacyImportError(`"${entry.name}" is too large to import`));
  return new Promise((resolve, reject) => {
    const chunks: Uint8Array[] = [];
    let total = 0;
    let done = false;
    const stream = (entry as StreamableEntry).internalStream('uint8array');
    stream
      .on('data', (chunk) => {
        if (done) return;
        total += chunk.length;
        if (total > limit) {
          done = true;
          stream.pause();
          reject(new LegacyImportError(`"${entry.name}" is too large to import`));
          return;
        }
        chunks.push(chunk);
      })
      .on('error', (err) => {
        if (done) return;
        done = true;
        reject(new LegacyImportError(`"${entry.name}" could not be decompressed: ${err.message}`));
      })
      .on('end', () => {
        if (done) return;
        done = true;
        const out = new Uint8Array(total);
        let offset = 0;
        for (const c of chunks) {
          out.set(c, offset);
          offset += c.length;
        }
        const expected = (entry as StreamableEntry)._data?.crc32;
        if (typeof expected !== 'number' || crc32(out) !== (expected >>> 0)) {
          reject(new LegacyImportError(`"${entry.name}" failed its ZIP integrity check.`));
          return;
        }
        resolve(out);
      });
    stream.resume();
  });
}

function baseName(path: string): string {
  const parts = path.split(/[\\/]/);
  return parts[parts.length - 1] ?? path;
}

function decodeUtf8(bytes: Uint8Array): string | null {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes).replace(/^\uFEFF/, '');
  } catch {
    return null;
  }
}

async function toBytes(input: Blob | ArrayBuffer | Uint8Array): Promise<Uint8Array> {
  // ArrayBuffer.isView also handles bytes supplied by another browser realm.
  if (ArrayBuffer.isView(input)) return new Uint8Array(input.buffer, input.byteOffset, input.byteLength);
  if (Object.prototype.toString.call(input) === '[object ArrayBuffer]') return new Uint8Array(input as ArrayBuffer);
  if (!('arrayBuffer' in input)) throw new LegacyImportError('The file is not a readable ZIP archive.');
  return new Uint8Array(await input.arrayBuffer());
}

/** Parse CSV text into records; quoted fields may contain commas, quotes and line breaks. */
export function parseCsvRecords(text: string): { header: string[]; records: LegacyFile['records'] } {
  const result = Papa.parse<string[]>(text, { header: false, skipEmptyLines: false, delimiter: ',' });
  const errorsByIndex = new Map<number, string>();
  for (const e of result.errors) {
    if (typeof e.row === 'number' && !errorsByIndex.has(e.row)) errorsByIndex.set(e.row, `Malformed CSV: ${e.message}`);
  }
  const data = result.data;
  const headerIndex = data.findIndex((r) => r.some((f) => normalizeWhitespace(f) !== ''));
  if (headerIndex < 0) return { header: [], records: [] };
  const header = data[headerIndex];
  const records: LegacyFile['records'] = [];
  for (let i = headerIndex + 1; i < data.length; i++) {
    const fields = data[i];
    if (fields.every((f) => normalizeWhitespace(f) === '') && !errorsByIndex.has(i)) continue;
    let error = errorsByIndex.get(i);
    if (!error && fields.length !== header.length)
      error = `Expected ${header.length} columns but found ${fields.length}`;
    records.push({ row: i - headerIndex, fields, ...(error ? { error } : {}) });
  }
  return { header, records };
}

/**
 * Open a legacy export ZIP with size/entry/row bounds and recognize its CSV
 * files by header. Throws LegacyImportError for unusable archives.
 */
export async function readLegacyZip(
  input: Blob | ArrayBuffer | Uint8Array,
  name: string,
  limits: ImportLimits = DEFAULT_LIMITS,
): Promise<LegacyArchive> {
  const size = input instanceof Blob ? input.size : input.byteLength;
  if (size === 0) throw new LegacyImportError('The selected file is empty.');
  if (size > limits.maxArchiveBytes)
    throw new LegacyImportError(`The archive is larger than the ${Math.round(limits.maxArchiveBytes / 1024 / 1024)} MB import limit.`);
  const bytes = await toBytes(input);
  const sha256 = await sha256Hex(bytes);

  let zip: JSZip;
  try {
    zip = await JSZip.loadAsync(bytes, { checkCRC32: false });
  } catch (err) {
    const detail = err instanceof Error && /encrypt/i.test(err.message) ? ' Encrypted archives are not supported.' : '';
    throw new LegacyImportError(`The file is not a readable ZIP archive.${detail}`);
  }

  const entries = Object.values(zip.files);
  if (entries.length > limits.maxEntries)
    throw new LegacyImportError(`The archive contains ${entries.length} entries; the limit is ${limits.maxEntries}.`);

  const files: LegacyFile[] = [];
  const skipped: FileProblem[] = [];
  const usedIds = new Set<string>();
  let totalBytes = 0;
  let totalRows = 0;

  for (const entry of entries.sort((a, b) => a.name.localeCompare(b.name))) {
    if (entry.dir) continue;
    const base = baseName(entry.name);
    if (entry.name.startsWith('__MACOSX/') || base.startsWith('.')) continue;
    if (!/\.csv$/i.test(base)) {
      skipped.push({ path: entry.name, reason: 'Not a CSV file' });
      continue;
    }
    const raw = await readEntryBounded(entry, Math.min(limits.maxEntryBytes, limits.maxTotalBytes - totalBytes));
    totalBytes += raw.length;
    const text = decodeUtf8(raw);
    if (text === null) {
      skipped.push({ path: entry.name, reason: 'File is not valid UTF-8 text' });
      continue;
    }
    const { header, records } = parseCsvRecords(text);
    const schema = detectSchema(header);
    if (!schema) {
      skipped.push({
        path: entry.name,
        reason: header.length
          ? `Unrecognized columns: ${header.map(normalizeWhitespace).join(', ')}`
          : 'File has no header row',
      });
      continue;
    }
    totalRows += records.length;
    if (totalRows > limits.maxRows)
      throw new LegacyImportError(`The archive contains more than ${limits.maxRows} rows, which exceeds the import limit.`);
    const fileId = normalizeWhitespace(base).toLowerCase();
    if (usedIds.has(fileId)) {
      skipped.push({ path: entry.name, reason: `Duplicate file name "${base}" in archive` });
      continue;
    }
    usedIds.add(fileId);
    const known = new Set(Object.keys(SCHEMAS[schema.type]));
    const extras = header.map(normalizeWhitespace).filter((h) => !known.has(h.toLowerCase()));
    files.push({
      path: entry.name,
      fileId,
      type: schema.type,
      columns: schema.columns,
      header,
      records,
      warnings: extras.length ? [`Ignored extra columns: ${extras.join(', ')}`] : [],
    });
  }

  if (!files.length) {
    throw new LegacyImportError(
      'No supported Baby Tracker CSV files were found. Expected formula, sleep or diaper exports with Baby, Time and Note columns.',
    );
  }
  files.sort((a, b) => TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type) || a.fileId.localeCompare(b.fileId));
  return { name, size, sha256, files, skipped };
}

// ---------------------------------------------------------------------------
// Analysis
// ---------------------------------------------------------------------------

export type AmbiguityChoice = 'earlier' | 'later' | 'skip';

interface RowBase {
  /** Unique within the archive: `${fileId}#${row}`. */
  ref: string;
  fileId: string;
  path: string;
  row: number;
  type: EventType;
}

export interface ParsedRow extends RowBase {
  sourceBaby: string;
  rawTime: string;
  /** Normalized fields used for the canonical identity. */
  canonical: string;
  note: string;
  amount_ml?: number;
  duration_min?: number;
  status?: DiaperStatus;
  /** Resolved instant, or both candidates when the local time is ambiguous. */
  time: { kind: 'ok'; utcMs: number } | { kind: 'ambiguous'; candidates: [number, number] };
}

export interface RejectedRow extends RowBase {
  reason: string;
  fields: string[];
}

export interface LegacyAnalysis {
  tz: string;
  rows: ParsedRow[];
  rejected: RejectedRow[];
  ambiguous: ParsedRow[];
  sourceBabies: string[];
  perFile: Array<{ path: string; fileId: string; type: EventType; total: number; valid: number; rejected: number; warnings: string[] }>;
}

function analyzeRecord(file: LegacyFile, rec: LegacyFile['records'][number], tz: string): ParsedRow | RejectedRow {
  const base: RowBase = { ref: `${file.fileId}#${rec.row}`, fileId: file.fileId, path: file.path, row: rec.row, type: file.type };
  const reject = (reason: string): RejectedRow => ({ ...base, reason, fields: rec.fields });
  if (rec.error) return reject(rec.error);
  const col = (c: Column) => {
    const idx = file.columns[c];
    return idx === undefined ? '' : (rec.fields[idx] ?? '');
  };

  const sourceBaby = col('baby');
  if (sourceBaby.length > 255) return reject('Baby name is longer than 255 characters');
  const rawTime = col('time');
  if (rawTime.length > 64) return reject('Time value is too long');
  const stamp = parseLegacyTimestamp(rawTime);
  if (typeof stamp === 'string') return reject(stamp);
  const note = normalizeNote(col('note'));
  if (note.length > 2000) return reject('Note is longer than 2000 characters');

  const resolved = resolveZoned(stamp, tz);
  if (resolved.kind === 'nonexistent')
    return reject(`Time "${normalizeWhitespace(rawTime)}" does not exist in ${tz} (daylight-saving gap)`);

  const row: ParsedRow = {
    ...base,
    sourceBaby,
    rawTime,
    note,
    canonical: '',
    time: resolved,
  };
  const canonicalTime = normalizeWhitespace(rawTime).toLowerCase();
  let valueKey: string;
  if (file.type === 'formula') {
    const amount = parseNonNegative(col('amount'), 'Amount', 'ml');
    if (typeof amount === 'string') return reject(amount);
    row.amount_ml = amount;
    valueKey = String(amount);
  } else if (file.type === 'sleep') {
    const duration = parseNonNegative(col('duration'), 'Duration', 'minutes');
    if (typeof duration === 'string') return reject(duration);
    if (duration > MAX_SLEEP_MINUTES) return reject(`Duration ${duration} minutes is longer than 24 hours`);
    row.duration_min = duration;
    valueKey = String(duration);
  } else {
    const rawStatus = normalizeWhitespace(col('status'));
    const status = mapDiaperStatus(rawStatus);
    if (!status) return reject(`Unknown diaper status "${rawStatus}"`);
    row.status = status;
    valueKey = status;
  }
  row.canonical = JSON.stringify([file.type, normalizeWhitespace(sourceBaby).toLowerCase(), canonicalTime, valueKey, note]);
  return row;
}

/** Validate every record against a timezone. Pure and cheap; re-run when the target baby (timezone) changes. */
export function analyzeArchive(archive: LegacyArchive, tz: string): LegacyAnalysis {
  const rows: ParsedRow[] = [];
  const rejected: RejectedRow[] = [];
  const perFile: LegacyAnalysis['perFile'] = [];
  const babies = new Set<string>();
  for (const file of archive.files) {
    let valid = 0;
    let bad = 0;
    for (const rec of file.records) {
      const r = analyzeRecord(file, rec, tz);
      if ('reason' in r) {
        rejected.push(r);
        bad++;
      } else {
        rows.push(r);
        if (r.sourceBaby) babies.add(r.sourceBaby);
        valid++;
      }
    }
    perFile.push({ path: file.path, fileId: file.fileId, type: file.type, total: file.records.length, valid, rejected: bad, warnings: file.warnings });
  }
  return {
    tz,
    rows,
    rejected,
    ambiguous: rows.filter((r) => r.time.kind === 'ambiguous'),
    sourceBabies: [...babies].sort(),
    perFile,
  };
}

/** Event start instant for a row given the caller's DST choices; null = unresolved, 'skip' = excluded by the user. */
export function resolveRowTime(row: ParsedRow, choices: Record<string, AmbiguityChoice>): number | null | 'skip' {
  if (row.time.kind === 'ok') return row.time.utcMs;
  const choice = choices[row.ref];
  if (!choice) return null;
  if (choice === 'skip') return 'skip';
  return choice === 'earlier' ? row.time.candidates[0] : row.time.candidates[1];
}

export function unresolvedAmbiguities(analysis: LegacyAnalysis, choices: Record<string, AmbiguityChoice>): ParsedRow[] {
  return analysis.ambiguous.filter((r) => !choices[r.ref]);
}

function csvCell(value: string | number): string {
  let s = String(value);
  // Neutralize spreadsheet formula injection from untrusted source text.
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** CSV report of rejected rows and skipped files, safe to open in a spreadsheet. */
export function rejectedReportCsv(
  archive: Pick<LegacyArchive, 'skipped'>,
  rejected: Array<Pick<RejectedRow, 'path' | 'row' | 'type' | 'reason' | 'fields'>>,
): string {
  const lines = [['file', 'row', 'type', 'reason', 'original_fields'].join(',')];
  for (const f of archive.skipped) lines.push([csvCell(f.path), '', '', csvCell(f.reason), ''].join(','));
  for (const r of rejected)
    lines.push([csvCell(r.path), r.row, r.type, csvCell(r.reason), csvCell(r.fields.join(' | '))].join(','));
  return lines.join('\r\n') + '\r\n';
}
